import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { translateAuthError } from "./AuthErrorTranslations";

interface ResetPasswordFormProps {
  onBack: () => void;
}

const ResetPasswordForm = ({ onBack }: ResetPasswordFormProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const { toast } = useToast();

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        toast({
          title: "Błąd",
          description: translateAuthError(error.message),
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Email wysłany",
        description: "Sprawdź swoją skrzynkę email, aby zresetować hasło",
      });

      onBack();
    } catch {
      toast({
        title: "Błąd",
        description: "Wystąpił nieoczekiwany błąd",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-foreground">Zresetuj hasło</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Podaj swój adres email, a wyślemy Ci link do resetowania hasła
        </p>
      </div>

      <form onSubmit={handlePasswordReset} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="reset-email">Email</Label>
          <Input
            id="reset-email"
            type="email"
            placeholder="twoj@email.com"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            required
            className="h-11"
          />
        </div>

        <Button
          type="submit"
          className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl font-medium"
          disabled={isLoading}
        >
          {isLoading ? "Wysyłanie..." : "Wyślij link resetujący"}
        </Button>

        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={onBack}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Powrót do logowania
        </Button>
      </form>
    </div>
  );
};

export default ResetPasswordForm;
